import React, { useState } from 'react';
import PropTypes from 'prop-types';
import './EmailPreview.css';
import UserDetailsModal from './UserDetailsModal';

/**
 * EmailPreview Component
 * Displays parsed emails for user review before uploading
 * Allows validation and selection of rows before upload
 */
const EmailPreview = ({ emailData, fileName, onConfirm, onCancel, onValidate, isValidating, isUploading, validationResults }) => {
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [expandedErrors, setExpandedErrors] = useState(new Set());
  const [showOverwriteWarning, setShowOverwriteWarning] = useState(false);
  const [existingRecordsCount, setExistingRecordsCount] = useState(0);

  /**
   * Handle select all checkbox
   * Only select rows that passed validation
   */
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRows(new Set());
    } else {
      // Only select valid rows
      const validIndices = emailData.rows
        .map((_, index) => index)
        .filter(index => {
          const validation = getValidationForRow(index);
          return validation && validation.isValid;
        });
      setSelectedRows(new Set(validIndices));
    }
    setSelectAll(!selectAll);
  };

  /**
   * Handle individual row checkbox
   * Prevent selection of invalid rows
   */
  const handleRowSelect = (rowIndex) => {
    const validation = getValidationForRow(rowIndex);

    // Prevent selection of invalid rows
    if (validation && !validation.isValid) {
      return;
    }

    const newSelected = new Set(selectedRows);
    if (newSelected.has(rowIndex)) {
      newSelected.delete(rowIndex);
    } else {
      newSelected.add(rowIndex);
    }
    setSelectedRows(newSelected);

    // Check if all valid rows are selected
    const validRowCount = emailData.rows.filter((_, index) => {
      const v = getValidationForRow(index);
      return v && v.isValid;
    }).length;
    setSelectAll(newSelected.size === validRowCount && validRowCount > 0);
  };

  /**
   * Get validation result for a specific row
   */
  const getValidationForRow = (rowIndex) => {
    if (!validationResults || validationResults.length === 0) {
      return null;
    }
    return validationResults.find(result => result.rowIndex === rowIndex);
  };

  /**
   * Handle email click to show user details modal
   */
  const handleEmailClick = (validation) => {
    if (validation.matchStatus === 'api_validated' && validation.apiValidation?.user_data) {
      setSelectedUser({
        userData: validation.apiValidation.user_data,
        email: validation.compareEmail || validation.email
      });
      setShowModal(true);
    }
  };

  /**
   * Toggle error details expansion for a row
   */
  const toggleErrorDetails = (rowIndex) => {
    const newExpanded = new Set(expandedErrors);
    if (newExpanded.has(rowIndex)) {
      newExpanded.delete(rowIndex);
    } else {
      newExpanded.add(rowIndex);
    }
    setExpandedErrors(newExpanded);
  };

  /**
   * Render match status badge
   */
  const renderMatchBadge = (rowIndex) => {
    const validation = getValidationForRow(rowIndex);

    if (!validation) {
      return <span className="match-badge match-pending">Not Validated</span>;
    }

    const isExpanded = expandedErrors.has(rowIndex);
    const hasErrorDetails = validation.message &&
      (validation.matchStatus === 'api_validation_failed' || validation.matchStatus === 'error');

    // Handle new Symphony API validation statuses
    if (validation.matchStatus === 'api_validated') {
      const userName = validation.apiValidation?.user_data?.UserName || 'User';
      return (
        <span
          className="match-badge match-api-validated"
          title={`Found in Symphony: ${userName}`}
        >
          ✓ API Validated
        </span>
      );
    } else if (validation.matchStatus === 'api_validation_failed') {
      return (
        <span
          className={`match-badge match-api-failed ${hasErrorDetails ? 'badge-clickable' : ''}`}
          title={hasErrorDetails ? "Click to view error details" : "Email not found in Symphony Summit"}
          onClick={(e) => {
            e.stopPropagation();
            if (hasErrorDetails) toggleErrorDetails(rowIndex);
          }}
          style={hasErrorDetails ? { cursor: 'pointer' } : {}}
        >
          ✗ Not Found {hasErrorDetails && <span className="dropdown-icon">{isExpanded ? '▲' : '▼'}</span>}
        </span>
      );
    } else if (validation.matchStatus === 'error') {
      return (
        <span
          className={`match-badge match-error ${hasErrorDetails ? 'badge-clickable' : ''}`}
          title={hasErrorDetails ? "Click to view error details" : 'Validation error'}
          onClick={(e) => {
            e.stopPropagation();
            if (hasErrorDetails) toggleErrorDetails(rowIndex);
          }}
          style={hasErrorDetails ? { cursor: 'pointer' } : {}}
        >
          Error {hasErrorDetails && <span className="dropdown-icon">{isExpanded ? '▲' : '▼'}</span>}
        </span>
      );
    }

    // Deprecated statuses (for backward compatibility)
    if (validation.matchStatus === 'same_user') {
      return <span className="match-badge match-yes">✓ Same User</span>;
    } else if (validation.matchStatus === 'different_user') {
      return <span className="match-badge match-no">✗ Different</span>;
    }

    return <span className="match-badge match-error">Unknown Status</span>;
  };

  /**
   * Handle validate button click
   */
  const handleValidate = async () => {
    if (onValidate) {
      await onValidate();
    }
  };

  /**
   * Check if any selected rows already exist in database
   */
  const checkForExistingRecords = () => {
    const selectedIndices = Array.from(selectedRows);
    const existingCount = selectedIndices.filter(index => {
      const validation = getValidationForRow(index);
      return validation?.hasDuplicates === true;
    }).length;

    return existingCount;
  };

  /**
   * Handle confirm upload
   * Check for existing records and show warning if needed
   */
  const handleConfirm = () => {
    const existingCount = checkForExistingRecords();

    if (existingCount > 0) {
      // Show warning dialog
      setExistingRecordsCount(existingCount);
      setShowOverwriteWarning(true);
    } else {
      // Proceed with upload
      proceedWithUpload();
    }
  };

  /**
   * Proceed with upload after confirmation
   */
  const proceedWithUpload = () => {
    const selectedIndices = Array.from(selectedRows);
    const selectedRowsData = emailData.rows.filter((_, index) => selectedRows.has(index));
    onConfirm(selectedRowsData, selectedIndices);
    setShowOverwriteWarning(false);
  };

  /**
   * Cancel the overwrite warning
   */
  const handleCancelOverwrite = () => {
    setShowOverwriteWarning(false);
    setExistingRecordsCount(0);
  };

  /**
   * Render database status badge
   */
  const renderDatabaseBadge = (rowIndex) => {
    const validation = getValidationForRow(rowIndex);

    if (!validation) {
      return <span className="db-badge db-pending">Not Validated</span>;
    }

    // Check if this row has any duplicates based on hasDuplicates flag
    if (validation.hasDuplicates === true) {
      return (
        <span
          className="db-badge db-exists"
          title="One or more emails in this row already exist in the database"
        >
          ⚠ Exists
        </span>
      );
    } else if (validation.hasDuplicates === false) {
      return (
        <span
          className="db-badge db-new"
          title="New emails - not in database"
        >
          ✓ New
        </span>
      );
    }

    return <span className="db-badge db-pending">Pending</span>;
  };

  /**
   * Render CSV table format
   */
  const renderCSVTable = () => {
    return (
      <div className="email-table-container">
        <table className="email-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAll}
                  aria-label="Select all rows"
                />
              </th>
              <th>#</th>
              {emailData.headers.map((header, index) => (
                <th key={index}>{header}</th>
              ))}
              <th>Match</th>
              <th>DB Status</th>
            </tr>
          </thead>
          <tbody>
            {emailData.rows.map((row, rowIndex) => {
              const validation = getValidationForRow(rowIndex);
              const isInvalid = validation && !validation.isValid;
              const rowClassName = `${selectedRows.has(rowIndex) ? 'row-selected' : ''} ${isInvalid ? 'row-invalid' : ''}`;
              const isExpanded = expandedErrors.has(rowIndex);
              const hasErrorDetails = validation?.message &&
                (validation?.matchStatus === 'api_validation_failed' || validation?.matchStatus === 'error');

              return (
                <React.Fragment key={rowIndex}>
                  <tr className={rowClassName}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedRows.has(rowIndex)}
                        onChange={() => handleRowSelect(rowIndex)}
                        disabled={isInvalid}
                        aria-label={`Select row ${rowIndex + 1}`}
                        title={isInvalid ? 'This row failed validation and cannot be uploaded' : ''}
                      />
                    </td>
                    <td className="row-number">{rowIndex + 1}</td>
                    {emailData.headers.map((header, colIndex) => {
                      const validation = getValidationForRow(rowIndex);
                      const isClickable = validation?.matchStatus === 'api_validated';
                      return (
                        <td
                          key={colIndex}
                          className={`email-cell ${isClickable ? 'email-clickable' : ''}`}
                          onClick={() => {
                            if (validation) handleEmailClick(validation);
                          }}
                          style={isClickable ? { cursor: 'pointer' } : {}}
                          title={isClickable ? 'Click to view Symphony user details' : ''}
                        >
                          {row[header] || '-'}
                        </td>
                      );
                    })}
                    <td className="match-cell">
                      {renderMatchBadge(rowIndex)}
                    </td>
                    <td className="db-status-cell">
                      {renderDatabaseBadge(rowIndex)}
                    </td>
                  </tr>
                  {isExpanded && hasErrorDetails && (
                    <tr className="error-details-row">
                      <td colSpan={emailData.headers.length + 4} className="error-details-cell">
                        <div className="error-details-content">
                          <strong>Error Details:</strong>
                          <p className="error-message-text">{validation.message}</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  /**
   * Render simple list format
   */
  const renderSimpleList = () => {
    return (
      <div className="email-list">
        {emailData.emails.map((email, index) => (
          <div key={index} className="email-item">
            <span className="email-number">{index + 1}.</span>
            <span className="email-address">{email}</span>
          </div>
        ))}
      </div>
    );
  };

  const totalCount = emailData.type === 'csv' ? emailData.totalRows : emailData.emails.length;
  const isCSV = emailData.type === 'csv';
  const hasValidationResults = validationResults && validationResults.length > 0;
  const selectedCount = selectedRows.size;

  return (
    <div className="email-preview-container">
      <div className="preview-header">
        <h2>Review Emails</h2>
        <p className="file-info">File: {fileName}</p>
        <p className="email-count">
          Found {totalCount} row{totalCount !== 1 ? 's' : ''}
          {isCSV && selectedCount > 0 && ` (${selectedCount} selected)`}
        </p>
      </div>

      <div className="email-list-container">
        {emailData.type === 'csv' ? renderCSVTable() : renderSimpleList()}
      </div>

      <div className="preview-actions">
        <button
          className="cancel-button"
          onClick={onCancel}
          disabled={isValidating || isUploading}
          aria-label="Cancel and go back"
        >
          Cancel
        </button>

        {isCSV && (
          <button
            className="validate-button"
            onClick={handleValidate}
            disabled={isValidating || isUploading}
            aria-label="Validate email rows"
          >
            {isValidating ? 'Validating...' : hasValidationResults ? 'Re-validate' : 'Validate'}
          </button>
        )}

        <button
          className="confirm-button"
          onClick={handleConfirm}
          disabled={isValidating || isUploading || (isCSV && selectedCount === 0) || (isCSV && !hasValidationResults)}
          aria-label="Confirm and upload emails"
          title={!hasValidationResults && isCSV ? 'Please validate emails before uploading' : ''}
        >
          {isUploading ? 'Uploading...' : `Upload ${isCSV ? `${selectedCount} Row${selectedCount !== 1 ? 's' : ''}` : `${totalCount} Row${totalCount !== 1 ? 's' : ''}`}`}
        </button>
      </div>

      {/* User Details Modal */}
      {showModal && selectedUser && (
        <UserDetailsModal
          userData={selectedUser.userData}
          email={selectedUser.email}
          onClose={() => setShowModal(false)}
        />
      )}

      {/* Overwrite Warning Modal */}
      {showOverwriteWarning && (
        <div
          className="modal-overlay"
          onClick={handleCancelOverwrite}
          onKeyDown={(e) => {
            if (e.key === 'Escape') handleCancelOverwrite();
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="warning-title"
          tabIndex={-1}
        >
          <div
            className="warning-modal"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.key === 'Escape' && handleCancelOverwrite()}
          >
            <div className="warning-modal-header">
              <div className="warning-icon">⚠</div>
              <h3 id="warning-title">Existing Records Warning</h3>
            </div>
            <div className="warning-modal-body">
              <p className="warning-message">
                <strong>{existingRecordsCount}</strong> of the selected records already exist in the database.
              </p>
              <p className="warning-details">
                Proceeding with this upload will <strong>overwrite</strong> the existing records with the new data.
                This action cannot be undone.
              </p>
              <div className="warning-question">
                Do you want to continue?
              </div>
            </div>
            <div className="warning-modal-actions">
              <button
                className="warning-cancel-button"
                onClick={handleCancelOverwrite}
                aria-label="Cancel upload"
              >
                Cancel
              </button>
              <button
                className="warning-confirm-button"
                onClick={proceedWithUpload}
                aria-label="Proceed with upload"
              >
                Yes, Overwrite Records
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

EmailPreview.propTypes = {
  emailData: PropTypes.shape({
    type: PropTypes.oneOf(['csv', 'text']).isRequired,
    headers: PropTypes.arrayOf(PropTypes.string),
    rows: PropTypes.arrayOf(PropTypes.object),
    emails: PropTypes.arrayOf(PropTypes.string),
    totalRows: PropTypes.number.isRequired
  }).isRequired,
  fileName: PropTypes.string.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onValidate: PropTypes.func,
  isValidating: PropTypes.bool,
  isUploading: PropTypes.bool,
  validationResults: PropTypes.arrayOf(PropTypes.object)
};

EmailPreview.defaultProps = {
  isValidating: false,
  isUploading: false,
  validationResults: null,
  onValidate: null
};

export default EmailPreview;
